import type { ValidationError } from "../lib/parse-validation-errors";
import { Alert } from "./ui";

type Props = {
  errors: ValidationError[] | null;
  // "panel" renders a labeled red panel (content-edit). "inline" is a tighter
  // list used in narrow columns (content-chat).
  variant?: "panel" | "inline";
};

// Display label for each error category. Unknown types fall through to the
// raw string so a brand-new server-side error code surfaces verbatim instead
// of being silently collapsed into a generic bucket.
const TYPE_LABELS: Record<string, string> = {
  frontmatter: "フロントマター",
  lint: "本文",
  server: "サーバー",
  unknown: "エラー",
};

export function ValidationErrors({ errors, variant = "panel" }: Props) {
  if (!errors || errors.length === 0) return null;
  const list = (
    <ul
      class={
        variant === "panel"
          ? "list-disc space-y-0.5 pl-6"
          : "space-y-1"
      }
    >
      {errors.map((e, i) => (
        <li key={i}>
          <span class="text-[11px] opacity-70">
            [{TYPE_LABELS[e.type] ?? e.type}]
          </span>{" "}
          {e.line ? `(${e.line} 行目) ` : ""}
          {e.message}
        </li>
      ))}
    </ul>
  );
  // Title shifts depending on which kind of error is present. Only the
  // frontmatter/lint paths are markdown-validation failures; everything else
  // (server crashes, 4xx error codes, unknown payloads) is a generic save
  // failure and should not be labelled "バリデーションエラー".
  const hasNonValidation = errors.some(
    (e) => e.type !== "frontmatter" && e.type !== "lint",
  );
  const panelTitle = hasNonValidation ? "保存に失敗しました" : "バリデーションエラー";

  return (
    <Alert
      tone="danger"
      title={variant === "panel" ? panelTitle : undefined}
    >
      {list}
    </Alert>
  );
}
